import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Driver } from '@typeRoots/common/drivers';

// Define the DriversState type
interface DriversState {
  drivers: Partial<Driver>[]; // List of drivers
  isDataSyncedToBackend: boolean; // Tracks unsaved changes
  currentDriver: Partial<Driver> | null; // Tracks the driver being added or edited
  showDriverToast: boolean;
}

// Initial state
const initialState: DriversState = {
  drivers: [], // Start with an empty array
  isDataSyncedToBackend: false, // No unsaved changes
  currentDriver: null, // No current driver initially
  showDriverToast: false,
};

// Create the slice
export const driversSlice = createSlice({
  name: 'drivers',
  initialState,
  reducers: {
    // Set drivers array from backend data
    setAllDrivers(state, action: PayloadAction<Partial<Driver>[]>) {
      state.drivers = action.payload;
      state.isDataSyncedToBackend = true; // No unsaved changes
    },

    // Add a new driver to the drivers array
    setDriver(state, action: PayloadAction<Partial<Driver>>) {
      // Add the new driver to the drivers array
      state.drivers.push(action.payload);
      state.isDataSyncedToBackend = true; // Mark as dirty due to the new addition
      state.currentDriver = null; // Clear currentDriver after adding
    },

    // Set the currentDriver when adding or editing a driver
    setCurrentDriver(state, action: PayloadAction<Partial<Driver>>) {
      state.currentDriver = action.payload; // Track current driver for add/edit
    },

    // Update a specific driver by merging its new properties
    updateDriver(state, action: PayloadAction<Partial<Driver> & { id: number }>) {
      const { id, ...updates } = action.payload;
      if (state.drivers) {
        const index = state.drivers.findIndex((driver) => driver.id === id);
        if (index !== -1) {
          state.drivers[index] = { ...state.drivers[index], ...updates };
          state.isDataSyncedToBackend = true;
        }
      }
      state.currentDriver = null; // Clear currentDriver after updating
    },

    // Mark drivers as saved
    updateIsSynced(state, action: PayloadAction<boolean>) {
      state.isDataSyncedToBackend = action.payload; // Data is now saved
    },

    // Clear a specific driver by its ID
    deleteDriver(state, action: PayloadAction<number>) {
      if (state.drivers) {
        state.drivers = state.drivers.filter((driver) => driver.id !== action.payload);
        state.isDataSyncedToBackend = true; //note that these will be called after successful api call
      }
      state.currentDriver = null; // Clear currentDriver after deleting
    },

    // Optionally, clear all drivers (if needed)
    clearAllDrivers(state) {
      state.drivers = [];
      state.isDataSyncedToBackend = false;
      state.currentDriver = null;
    },
    toggleDriverToast(state, action: PayloadAction<boolean>) {
      state.showDriverToast = action.payload;
    },
  },
});

// Export actions
export const {
  setAllDrivers,
  updateDriver,
  updateIsSynced,
  deleteDriver,
  setDriver,
  setCurrentDriver,
  clearAllDrivers,
  toggleDriverToast,
} = driversSlice.actions;

// Export reducer
export default driversSlice.reducer;
